import { Button, theme } from "antd";
import { FileDoneOutlined, FileSyncOutlined, FileTextOutlined } from "@ant-design/icons";
import { ModalAppearanceProps } from "../modalProps";
import { SearchPatientForm } from "../SearchPatientForm/SearchPatientForm";
import { TakePaymentRadioTabs } from "../TakePaymentRadioTabs/TakePaymentRadioTabs";
import { VyneModal } from "@vyne-shared/components/vyneModal/VyneModal";
import { antdModalV5 } from "@ebay/nice-modal-react";
import { useSearchFlowContext } from "./useSearchFlow";
import { useSyncStatus } from "api/clients/useSyncStatus";
import type { TakePaymentTabType } from "../TakePaymentModal";

const { useToken } = theme;

export interface SearchFlowModalContentProps extends Readonly<React.PropsWithChildren> {
    /** Footer to display once a patient has been selected. */
    readonly footer?: React.ReactNode;

    /** Title of the dialog. */
    readonly title: string;

    /** Flag indicating patients should be selectable by recent appointment in addition to search. */
    readonly showRecentVisits?: boolean;

    /** Set the current tab in the Take Payment modal. */
    readonly setTakePaymentTab?: (tab: TakePaymentTabType) => void;
}

/**
 * Provides flow for searching for a patient and then performing some work.
 *
 * The modal approach (grabbing a previously defined modal instance from useSearchFlowContext) allows NewPaymentModal
 * to own the modal lifecycle, but with the visible contents (antd Modal, including its title, body, and footer) being
 * swappable between various components, such as this one and MergeFlowModalContent and InsurancePaymentTab.
 */
export const PatientSearchTab = ({
    footer,
    title,
    showRecentVisits,
    children,
    setTakePaymentTab,
}: Readonly<SearchFlowModalContentProps>) => {
    const { modal, onSelectExistingPrp, searchComplete, onSelectNewPrp } = useSearchFlowContext();

    return (
        <VyneModal
            {...antdModalV5(modal)}
            title={<SearchFlowModalTitle title={title} searchComplete={searchComplete} />}
            style={{ top: 20 }}
            footer={searchComplete ? footer : <CancelOnlyFooter onCancel={() => void modal.hide()} />}
            {...ModalAppearanceProps}
        >
            {!!setTakePaymentTab && (
                <TakePaymentRadioTabs setTakePaymentTab={setTakePaymentTab} takePaymentTab="patient-payment" />
            )}
            {searchComplete ? (
                children
            ) : (
                <SearchPatientForm
                    onSelectExistingPrp={onSelectExistingPrp}
                    onSelectNewPrp={onSelectNewPrp}
                    showRecentVisits={showRecentVisits}
                />
            )}
        </VyneModal>
    );
};

interface SearchFlowModalTitleProps {
    readonly title: string;
    readonly searchComplete: boolean;
}

const SearchFlowModalTitle = ({ title, searchComplete }: SearchFlowModalTitleProps) => (
    <>
        {title}
        <span style={{ opacity: 0.6, position: "relative", left: 6, cursor: "help" }}>
            <SearchFlowModalTitleIcon searchComplete={searchComplete} />
        </span>
    </>
);

const SearchFlowModalTitleIcon = ({ searchComplete }: { readonly searchComplete: boolean }) => {
    const status = useSyncStatus(searchComplete);
    const { token } = useToken();

    switch (status) {
        case "current":
            // data is fresh from VDS API on-demand pull for this login session
            // in the tooltip we express this as "recent", as even with fresh data, PMS integration is not real-time
            return (
                <FileDoneOutlined
                    title="Using recent data from your practice software"
                    style={{ color: token.colorPrimary }}
                />
            );
        case "recent":
            return (
                <FileTextOutlined
                    title="Using data from your practice software"
                    style={{ color: token.colorPrimary }}
                />
            );
        case "inprogress":
            return <FileSyncOutlined title="Getting data from your practice software" />;
        default:
            return null;
    }
};

const CancelOnlyFooter = ({ onCancel }: { readonly onCancel: () => void }) => (
    <Button onClick={onCancel}>Cancel</Button>
);
